/**
 * Helpers del módulo Entregables.
 *
 * RLS aplica sobre todas las tablas:
 *   - alumno   → ve/edita SOLO su entregable
 *   - docente  → lee entregables de cursos donde es docente/secondary
 *   - admin    → todo
 *
 * El frontend no necesita filtrar por user — las policies hacen el trabajo.
 */
import { supabase } from './supabase';
import type {
  CourseDeliverable,
  DeliverableProgress,
  DeliverableRow,
  DeliverableSheet,
  DeliverableTemplate,
  DeliverableTemplateRow,
} from '../types/deliverables';

/** Plantilla por defecto (la "PLANTILLA MODELO BIM CUBUS"). */
export async function getDefaultTemplate(): Promise<DeliverableTemplate | null> {
  const { data, error } = await supabase
    .from('deliverable_templates')
    .select('*')
    .eq('is_default', true)
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error('[deliverables] getDefaultTemplate:', error);
    return null;
  }
  return data;
}

export async function listTemplates(): Promise<DeliverableTemplate[]> {
  const { data, error } = await supabase
    .from('deliverable_templates')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[deliverables] listTemplates:', error);
    return [];
  }
  return data ?? [];
}

export async function listTemplateRows(templateId: string): Promise<DeliverableTemplateRow[]> {
  const { data, error } = await supabase
    .from('deliverable_template_rows')
    .select('*')
    .eq('template_id', templateId)
    .order('sort_order', { ascending: true });
  if (error) {
    console.error('[deliverables] listTemplateRows:', error);
    return [];
  }
  return data ?? [];
}

/** Busca el entregable del alumno actual en un curso, o null si no existe. */
export async function getMyDeliverable(courseId: string): Promise<CourseDeliverable | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('course_deliverables')
    .select('*')
    .eq('course_id', courseId)
    .eq('student_id', user.id)
    .maybeSingle();
  if (error) {
    console.error('[deliverables] getMyDeliverable:', error);
    return null;
  }
  return data;
}

/**
 * Crea el entregable del alumno + clona TODAS las filas de la plantilla.
 * Idempotente: si ya existe lo retorna sin tocar nada.
 */
export async function createDeliverable(courseId: string, templateId?: string): Promise<CourseDeliverable | null> {
  // ¿Ya existe?
  const existing = await getMyDeliverable(courseId);
  if (existing) return existing;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // Resolver template (default si no se pasó)
  let tplId = templateId;
  if (!tplId) {
    const tpl = await getDefaultTemplate();
    if (!tpl) {
      console.error('[deliverables] createDeliverable: no hay plantilla default');
      return null;
    }
    tplId = tpl.id;
  }

  // Insert del header
  const { data: created, error: insErr } = await supabase
    .from('course_deliverables')
    .insert({
      course_id:  courseId,
      student_id: user.id,
      template_id: tplId,
    })
    .select('*')
    .single();
  if (insErr || !created) {
    console.error('[deliverables] createDeliverable insert header:', insErr);
    return null;
  }

  // Clonar filas
  const tplRows = await listTemplateRows(tplId);
  if (tplRows.length > 0) {
    const payload = tplRows.map((tr) => ({
      deliverable_id:  created.id,
      template_row_id: tr.id,
      sheet:           tr.sheet,
      section:         tr.section,
      subsection:      tr.subsection,
      activity:        tr.activity,
      is_leveled:      tr.is_leveled,
      // Extraer el N del final si es leveled (ej: "N1" → 1)
      level_index:     tr.is_leveled ? extractLevelIndex(tr.activity) : null,
      delivery_e1:     tr.delivery_e1,
      delivery_e2:     tr.delivery_e2,
      delivery_e3:     tr.delivery_e3,
      lod:             tr.lod,
      file_format:     tr.file_format,
      scales:          tr.scales,
      location:        tr.location,
      sort_order:      tr.sort_order,
    }));
    const { error: rowsErr } = await supabase.from('deliverable_rows').insert(payload);
    if (rowsErr) {
      console.error('[deliverables] createDeliverable insert rows:', rowsErr);
      // Borramos el header para que el siguiente intento parta limpio
      await supabase.from('course_deliverables').delete().eq('id', created.id);
      return null;
    }
  }

  return created;
}

function extractLevelIndex(activity: string): number | null {
  const m = activity.match(/\sN\s*(\d+)\s*$/i);
  return m ? parseInt(m[1], 10) : null;
}

export async function listDeliverableRows(deliverableId: string): Promise<DeliverableRow[]> {
  const { data, error } = await supabase
    .from('deliverable_rows')
    .select('*')
    .eq('deliverable_id', deliverableId)
    .order('sort_order', { ascending: true });
  if (error) {
    console.error('[deliverables] listDeliverableRows:', error);
    return [];
  }
  return data ?? [];
}

/** Actualiza header del entregable (project_name, dates, etc.). */
export async function updateDeliverableHeader(
  deliverableId: string,
  patch: Partial<Pick<CourseDeliverable, 'student_name' | 'project_name' | 'project_code' | 'project_date'>>,
): Promise<boolean> {
  const { error } = await supabase
    .from('course_deliverables')
    .update(patch)
    .eq('id', deliverableId);
  if (error) {
    console.error('[deliverables] updateDeliverableHeader:', error);
    return false;
  }
  return true;
}

/** Actualiza una fila (status_eN, notes, percent_status). */
export async function updateRow(
  rowId: string,
  patch: Partial<Pick<DeliverableRow, 'status_e1' | 'status_e2' | 'status_e3' | 'notes' | 'percent_status'>>,
): Promise<boolean> {
  const { error } = await supabase
    .from('deliverable_rows')
    .update(patch)
    .eq('id', rowId);
  if (error) {
    console.error('[deliverables] updateRow:', error);
    return false;
  }
  return true;
}

/**
 * Clona una fila con nivel para agregar el siguiente nivel.
 * Ej: si existe "Modelado Muros Arq/Est N3", el siguiente sería N4.
 *
 * @param row Fila base desde la cual clonamos (cualquiera del grupo).
 * @param existingMaxLevel Mayor level_index ya presente en el grupo.
 */
export async function addLeveledRow(
  row: DeliverableRow,
  existingMaxLevel: number,
): Promise<DeliverableRow | null> {
  if (!row.is_leveled) return null;
  const nextLevel = existingMaxLevel + 1;

  // Base name = activity sin el "Nx" final
  const baseName = row.activity.replace(/\sN\s*\d+\s*$/i, ' N');
  const newActivity = `${baseName}${nextLevel}`;

  // sort_order: insertamos justo después de la última fila del grupo
  // (sumamos 1 al actual; los demás índices siguen sin chocar porque van de 10 en 10)
  const newSortOrder = row.sort_order + 1;

  const { data, error } = await supabase
    .from('deliverable_rows')
    .insert({
      deliverable_id:  row.deliverable_id,
      template_row_id: null,    // custom — no viene de plantilla
      sheet:           row.sheet,
      section:         row.section,
      subsection:      row.subsection,
      activity:        newActivity,
      is_leveled:      true,
      level_index:     nextLevel,
      delivery_e1:     row.delivery_e1,
      delivery_e2:     row.delivery_e2,
      delivery_e3:     row.delivery_e3,
      lod:             row.lod,
      file_format:     row.file_format,
      scales:          row.scales,
      location:        row.location,
      sort_order:      newSortOrder,
    })
    .select('*')
    .single();
  if (error) {
    console.error('[deliverables] addLeveledRow:', error);
    return null;
  }
  return data;
}

export async function removeRow(rowId: string): Promise<boolean> {
  const { error } = await supabase
    .from('deliverable_rows')
    .delete()
    .eq('id', rowId);
  if (error) {
    console.error('[deliverables] removeRow:', error);
    return false;
  }
  return true;
}

// ────────────────────────────────────────────────────────────────────────
// Progreso — para docente y para mostrar % en el header del alumno
// ────────────────────────────────────────────────────────────────────────

/**
 * Una fila se considera "completada" cuando todos los status_eN aplicables
 * (los que delivery_eN === true) están en true.
 *
 * Ej: si la fila aplica solo a E1 (delivery_e1=true, delivery_e2=false,
 *     delivery_e3=false), basta con status_e1=true.
 *     Si aplica a E1 y E3, se requiere status_e1 && status_e3.
 *
 * Si la fila no aplica a ninguna entrega (sin tildes), la consideramos
 * "n/a" — no cuenta para el denominador ni para el numerador.
 */
export function isRowDone(r: DeliverableRow): boolean | 'na' {
  const applies = [r.delivery_e1, r.delivery_e2, r.delivery_e3];
  const statuses = [r.status_e1, r.status_e2, r.status_e3];
  const idx = applies.map((a, i) => (a ? i : -1)).filter((i) => i >= 0);
  if (idx.length === 0) return 'na';
  return idx.every((i) => statuses[i]);
}

export function computeProgress(rows: DeliverableRow[]): { total: number; done: number; percent: number } {
  let total = 0;
  let done = 0;
  for (const r of rows) {
    const d = isRowDone(r);
    if (d === 'na') continue;
    total += 1;
    if (d === true) done += 1;
  }
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return { total, done, percent };
}

/**
 * Progreso basado en el campo "ESTADO" (percent_status) — promedio simple
 * sobre TODAS las filas. Más representativo que isRowDone cuando el alumno
 * usa el % manual en vez de los checkboxes E1/E2/E3.
 */
export function computeAveragePercent(rows: DeliverableRow[]): number {
  if (rows.length === 0) return 0;
  const sum = rows.reduce((acc, r) => acc + (r.percent_status ?? 0), 0);
  return Math.round(sum / rows.length);
}

/** Para vista de docente: lista alumnos del curso + su % de progreso. */
export async function listCourseProgress(courseId: string): Promise<DeliverableProgress[]> {
  // Traemos los entregables del curso (RLS deja al docente verlos)
  const { data: deliverables, error } = await supabase
    .from('course_deliverables')
    .select('id, student_id, student_name, project_name, updated_at')
    .eq('course_id', courseId);
  if (error) {
    console.error('[deliverables] listCourseProgress headers:', error);
    return [];
  }
  if (!deliverables?.length) return [];

  const ids = deliverables.map((d: any) => d.id);
  const { data: allRows, error: rowsErr } = await supabase
    .from('deliverable_rows')
    .select('deliverable_id, delivery_e1, delivery_e2, delivery_e3, status_e1, status_e2, status_e3')
    .in('deliverable_id', ids);
  if (rowsErr) {
    console.error('[deliverables] listCourseProgress rows:', rowsErr);
    return [];
  }

  const byDeliv = new Map<string, any[]>();
  for (const r of (allRows ?? [])) {
    const arr = byDeliv.get(r.deliverable_id) ?? [];
    arr.push(r);
    byDeliv.set(r.deliverable_id, arr);
  }

  return deliverables.map((d: any) => {
    const rows = (byDeliv.get(d.id) ?? []) as DeliverableRow[];
    const { total, done, percent } = computeProgress(rows);
    return {
      deliverable_id: d.id,
      student_id:     d.student_id,
      student_name:   d.student_name,
      project_name:   d.project_name,
      total_rows:     total,
      done_rows:      done,
      percent,
      updated_at:     d.updated_at,
    };
  });
}
